import type { MutableRefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import type { Nullable } from 'corgi-types';

interface UseLockSearchParams {
  setLocked: () => void;
  searchContainerRef: MutableRefObject<Nullable<HTMLDivElement>>;
}

interface UseUnlockSearchParams {
  setUnlocked: () => void;
  searchContainerRef: MutableRefObject<Nullable<HTMLDivElement>>;
  searchPlaceholderRef: MutableRefObject<Nullable<HTMLDivElement>>;
}

const blurActiveElement = () => {
  const active = document.activeElement as HTMLElement;
  if (active) {
    active.blur();
  }
};

const useLockSearch = ({ setLocked, searchContainerRef }: UseLockSearchParams) =>
  useCallback(() => {
    // Before "locked" animation kicks in, we will adjust element top
    // position for animation purposes. As the element has "static" position
    // applied on locked = false, this will not affect it in any way before
    // "entering" phase starts
    if (searchContainerRef.current) {
      const rect = searchContainerRef.current.getBoundingClientRect();
      searchContainerRef.current.style.willChange = 'top, background-color';
      searchContainerRef.current.style.top = `${rect.top}px`;
      setLocked();
    }
  }, []);

const useUnlockSearch = ({
  setUnlocked,
  searchContainerRef,
  searchPlaceholderRef,
}: UseUnlockSearchParams) =>
  useCallback(() => {
    // Before "unlocking" screen, we will adjust the top position
    // to the current placeholder position, so the form animates
    // back to the appropriate place
    if (searchPlaceholderRef.current && searchContainerRef.current) {
      const rect = searchPlaceholderRef.current.getBoundingClientRect();
      searchContainerRef.current.style.top = `${rect.top}px`;
      setUnlocked();
      blurActiveElement();
    }
  }, []);

const useSearchLock = () => {
  const searchContainerRef = useRef<Nullable<HTMLDivElement>>(null);
  const searchPlaceholderRef = useRef<Nullable<HTMLDivElement>>(null);
  const [isLocked, setLocked] = useState(false);
  const lockSearch = useLockSearch({
    setLocked: () => setLocked(true),
    searchContainerRef,
  });

  const unlockSearch = useUnlockSearch({
    setUnlocked: () => setLocked(false),
    searchContainerRef,
    searchPlaceholderRef,
  });

  return {
    isLocked,
    searchContainerRef,
    searchPlaceholderRef,
    lockSearch,
    unlockSearch,
  };
};

export default useSearchLock;
